import { AssetsApi } from '../api/assetsApi';
import { File } from '../interfaces/File';
import { AxiosResponse } from 'axios';
import React, { createContext, useState } from 'react';

interface FileContextProps {
	selectedFiles: File[];
	isFileUploading: boolean;
	uploadFile: (files: File) => void;
	deleteFile: (fileId?: string) => void;
	getFile: (fileId: string) => Promise<AxiosResponse>;
	clearSelectedFiles: () => void;
}

export const FileContext = createContext<FileContextProps>({
	selectedFiles: [],
	isFileUploading: false,
	uploadFile: () => null,
	deleteFile: () => null,
	getFile: () => new Promise(() => null),
	clearSelectedFiles: () => null,
});

export const FileContextProvider = ({ children }: { children: React.ReactNode }) => {
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
	const [isDeletingFile, setIsDeletingFile] = useState<boolean>(false);

	const createNewFile = (file: File, fileId: string): File => ({
		name: file.name,
		path: file.path,
		bytes: file.size,
		id: fileId,
		progress: {
			loading: true,
			percent: 0,
		},
	});

	const uploadFile = (file: File) => {
		setIsFileUploading(true);
		AssetsApi.uploadFile(file)
			.then(response => {
				switch (response.status) {
					case 202: {
						const fileIdUrl = response.data;
						const fileId = fileIdUrl.slice(15);
						const newFile = createNewFile(file, fileId);
						setSelectedFiles([newFile]);
						break;
					}
					default: {
						setSelectedFiles([]);
						break;
					}
				}
			})
			.catch(error => {
				setSelectedFiles([]);
				console.error(error);
			})
			.finally(() => setIsFileUploading(false));
	};

	const deleteFile = (fileId?: string) => {
		setSelectedFiles([]);
		if (fileId && !isDeletingFile) {
			setIsDeletingFile(true);
			AssetsApi.deleteFile(fileId).finally(() => setIsDeletingFile(false));
		}
	};

	const getFile = (fileId: string): Promise<AxiosResponse> => {
		return AssetsApi.getFile(fileId)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	};

	const clearSelectedFiles = () => setSelectedFiles([]);

	return (
		<FileContext.Provider
			value={{ selectedFiles, isFileUploading, uploadFile, deleteFile, getFile, clearSelectedFiles }}
		>
			{children}
		</FileContext.Provider>
	);
};
